import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import Loading from '../../../../molecules/Loading'
import { actionTemplates } from '../../../../../api/coreData'
// TODO: promote diffLines to a shared utility
import { diffLines } from '../../Analysis/SmartEndpoints/AgentSidePanel/blocks/diffUtils'

const useStyles = makeStyles(() => ({
  diffContainer: {
    display: 'flex',
    gap: '2px',
    maxHeight: '500px',
    overflow: 'auto',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: 1.6,
    backgroundColor: 'var(--color-neutral-050)',
    borderRadius: '6px',
    border: '1px solid var(--color-neutral-200)'
  },
  diffPane: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto'
  },
  diffHeader: {
    padding: '6px 12px',
    fontSize: '11px',
    fontWeight: 700,
    backgroundColor: 'var(--color-neutral-100)',
    borderBottom: '1px solid var(--color-neutral-200)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    color: 'var(--text-color-primary)'
  },
  diffLine: {
    padding: '0 12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  },
  diffAdded: {
    backgroundColor: 'rgba(46, 160, 67, 0.12)',
    color: 'var(--text-color-primary)'
  },
  diffRemoved: {
    backgroundColor: 'rgba(248, 81, 73, 0.12)',
    color: 'var(--text-color-primary)'
  },
  diffEqual: {
    color: 'var(--text-color-subtle)'
  },
  divider: {
    width: '2px',
    backgroundColor: 'var(--color-neutral-200)',
    flexShrink: 0
  },
  stats: {
    display: 'flex',
    gap: '12px',
    padding: '8px 0',
    fontSize: '12px'
  },
  statAdded: {
    color: 'var(--color-success-600)',
    fontWeight: 600
  },
  statRemoved: {
    color: 'var(--color-danger-500)',
    fontWeight: 600
  },
  noDiff: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    fontSize: 'var(--font-size-sm)'
  }
}))

function VersionDiffModal ({ open, onClose, actionTemplateId, leftVersionId, rightVersionId }) {
  const classes = useStyles()

  const { data: leftDef, isLoading: leftLoading, error: leftError } = actionTemplates.useGetActionTemplateDefinition(
    { actionTemplateId, definitionVersionId: leftVersionId },
    { enabled: open && !!leftVersionId }
  )

  const { data: rightDef, isLoading: rightLoading, error: rightError } = actionTemplates.useGetActionTemplateDefinition(
    { actionTemplateId, definitionVersionId: rightVersionId },
    { enabled: open && !!rightVersionId }
  )

  const isLoading = leftLoading || rightLoading
  const fetchError = leftError || rightError

  const { leftLines, rightLines, addedCount, removedCount } = useMemo(() => {
    if (!leftDef || !rightDef) return { leftLines: [], rightLines: [], addedCount: 0, removedCount: 0 }

    const leftText = JSON.stringify(leftDef, null, 2)
    const rightText = JSON.stringify(rightDef, null, 2)
    const ops = diffLines(leftText.split('\n'), rightText.split('\n'))

    const left = []
    const right = []
    let added = 0
    let removed = 0

    for (const op of ops) {
      if (op.type === 'equal') {
        left.push({ type: 'equal', text: op.old })
        right.push({ type: 'equal', text: op.new })
      } else if (op.type === 'removed') {
        left.push({ type: 'removed', text: op.old })
        right.push({ type: 'pad', text: '' })
        removed++
      } else {
        left.push({ type: 'pad', text: '' })
        right.push({ type: 'added', text: op.new })
        added++
      }
    }

    return { leftLines: left, rightLines: right, addedCount: added, removedCount: removed }
  }, [leftDef, rightDef])

  const hasDiff = addedCount > 0 || removedCount > 0

  const renderLines = useCallback((lines) => lines.map((line, i) => {
    let cls = classes.diffEqual
    if (line.type === 'added') cls = classes.diffAdded
    else if (line.type === 'removed') cls = classes.diffRemoved
    else if (line.type === 'pad') cls = ''
    return (
      <div key={i} className={`${classes.diffLine} ${cls}`}>
        {line.text || '\u00A0'}
      </div>
    )
  }), [classes])

  return (
    <SydModal
      title={`Compare ${leftVersionId || '?'} \u2192 ${rightVersionId || '?'}`}
      open={open}
      onClose={onClose}
      size='xlarge'
      position='top'
    >
      <div style={{ padding: '16px' }}>
        {isLoading ? (
          <Loading />
        ) : fetchError ? (
          <div className={classes.noDiff}>Failed to load version data for comparison.</div>
        ) : !hasDiff ? (
          <div className={classes.noDiff}>These two versions are identical.</div>
        ) : (
          <>
            <div className={classes.stats}>
              <span className={classes.statAdded}>+{addedCount} added</span>
              <span className={classes.statRemoved}>&minus;{removedCount} removed</span>
            </div>
            <div className={classes.diffContainer}>
              <div className={classes.diffPane}>
                <div className={classes.diffHeader}>{leftVersionId}</div>
                {renderLines(leftLines)}
              </div>
              <div className={classes.divider} />
              <div className={classes.diffPane}>
                <div className={classes.diffHeader}>{rightVersionId}</div>
                {renderLines(rightLines)}
              </div>
            </div>
          </>
        )}
        <SydModalActions>
          <SydButton variant='primary' onClick={onClose}>Close</SydButton>
        </SydModalActions>
      </div>
    </SydModal>
  )
}

VersionDiffModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  actionTemplateId: PropTypes.number,
  leftVersionId: PropTypes.string,
  rightVersionId: PropTypes.string
}

export default VersionDiffModal
